/**
 * Zuordnungs-Sektion für einen Mandanten: Marke + MSP-Kontext + Kunde.
 * Wird als Sektion in TenantDetailPage eingehängt.
 *
 *   - PUT /api/tenants/:slug/brand    { brand }               (Marke setzen)
 *   - PUT /api/tenants/:slug/context  { contextId | null }    (Kontext zuordnen/lösen)
 *   - PUT /api/tenants/:slug/customer { customerId | null }   (Kunde zuordnen/lösen)
 *
 * Alle Endpunkte antworten 204; nach Erfolg lädt die Detailseite via
 * onChanged() neu, damit die aktuelle Zuordnung konsistent angezeigt wird.
 * Auswahllisten kommen aus /api/brands, /api/contexts bzw. /api/customers;
 * bei Kontexten/Kunden nur aktive (archivierte nehmen keine neuen Mandanten
 * auf). Kontext = technische Provisioning-Gruppierung, Kunde = kommerzielle
 * Entität (ein Kunde kann mehrere Deployments besitzen).
 */
import { useEffect, useState } from 'react';
import api, {
  BrandView, ContextView, CustomerView,
  extractApiError, listBrands, listContexts, listCustomers,
} from '../services/api';

interface Props {
  slug: string;
  currentBrand: string;
  currentContextId: string | null;
  currentCustomerId: string | null;
  isReadOnly: boolean;
  onChanged: () => void;
}

export function TenantAssignmentSection({
  slug, currentBrand, currentContextId, currentCustomerId, isReadOnly, onChanged,
}: Props) {
  const [brands, setBrands] = useState<BrandView[]>([]);
  const [contexts, setContexts] = useState<ContextView[]>([]);
  const [customers, setCustomers] = useState<CustomerView[]>([]);
  const [brand, setBrand] = useState(currentBrand);
  const [contextId, setContextId] = useState<string>(currentContextId ?? '');
  const [customerId, setCustomerId] = useState<string>(currentCustomerId ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    listBrands().then(setBrands).catch(() => setBrands([]));
    listContexts().then(setContexts).catch(() => setContexts([]));
    listCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  useEffect(() => { setBrand(currentBrand); }, [currentBrand]);
  useEffect(() => { setContextId(currentContextId ?? ''); }, [currentContextId]);
  useEffect(() => { setCustomerId(currentCustomerId ?? ''); }, [currentCustomerId]);

  const saveBrand = async () => {
    if (isReadOnly || brand === currentBrand) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.put(`/tenants/${slug}/brand`, { brand });
      setMsg('Marke aktualisiert.');
      onChanged();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveContext = async () => {
    if (isReadOnly || contextId === (currentContextId ?? '')) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.put(`/tenants/${slug}/context`, { contextId: contextId === '' ? null : contextId });
      setMsg('Kontext-Zuordnung aktualisiert.');
      onChanged();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveCustomer = async () => {
    if (isReadOnly || customerId === (currentCustomerId ?? '')) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.put(`/tenants/${slug}/customer`, { customerId: customerId === '' ? null : customerId });
      setMsg('Kunden-Zuordnung aktualisiert.');
      onChanged();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Aktive Kontexte + (falls aktuell zugeordnet) der bestehende, auch wenn
  // archiviert — damit die aktuelle Zuordnung sichtbar bleibt.
  const selectableContexts = contexts.filter(
    (c) => c.status === 'active' || c.id === currentContextId,
  );
  // Gleiches Prinzip für Kunden: aktive + (falls zugeordnet) der bestehende.
  const selectableCustomers = customers.filter(
    (c) => c.status === 'active' || c.id === currentCustomerId,
  );

  return (
    <section className="mb-6">
      <h3 className="font-semibold mb-2">Zuordnung (Marke, MSP-Kontext &amp; Kunde)</h3>
      <p className="text-xs text-slate-500 mb-3">
        Ordnet dieses Deployment einer White-Label-Marke, optional einem
        MSP-Kontext (technische Provisioning-Gruppierung) und einem Kunden
        (kommerzielle Entität — ein Kunde kann mehrere Deployments besitzen)
        zu.
      </p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
      {msg && <div className="bg-green-50 text-green-800 p-3 rounded mb-3 text-sm">{msg}</div>}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4 space-y-2">
          <label className="block text-sm text-slate-600">Marke</label>
          <div className="flex items-center gap-2">
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              disabled={isReadOnly}
              className="flex-1 px-2 py-1 rounded border text-sm"
            >
              {/* Aktuelle Marke immer als Option, auch wenn nicht in der Liste. */}
              {!brands.some((b) => b.slug === brand) && <option value={brand}>{brand}</option>}
              {brands.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {b.displayName} ({b.slug}){b.status !== 'active' ? ' — archiviert' : ''}
                </option>
              ))}
            </select>
            <button onClick={saveBrand} disabled={busy || isReadOnly || brand === currentBrand} className="btn-primary">
              Setzen
            </button>
          </div>
        </div>

        <div className="bg-white rounded shadow p-4 space-y-2">
          <label className="block text-sm text-slate-600">MSP-Kontext</label>
          <div className="flex items-center gap-2">
            <select
              value={contextId}
              onChange={(e) => setContextId(e.target.value)}
              disabled={isReadOnly}
              className="flex-1 px-2 py-1 rounded border text-sm"
            >
              <option value="">— kein Kontext —</option>
              {selectableContexts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.brand}){c.status !== 'active' ? ' — archiviert' : ''}
                </option>
              ))}
            </select>
            <button
              onClick={saveContext}
              disabled={busy || isReadOnly || contextId === (currentContextId ?? '')}
              className="btn-primary"
            >
              Setzen
            </button>
          </div>
        </div>

        <div className="bg-white rounded shadow p-4 space-y-2">
          <label className="block text-sm text-slate-600">Kunde</label>
          <div className="flex items-center gap-2">
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              disabled={isReadOnly}
              className="flex-1 px-2 py-1 rounded border text-sm"
            >
              <option value="">— kein Kunde —</option>
              {selectableCustomers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName} ({c.brand}){c.status !== 'active' ? ' — archiviert' : ''}
                </option>
              ))}
            </select>
            <button
              onClick={saveCustomer}
              disabled={busy || isReadOnly || customerId === (currentCustomerId ?? '')}
              className="btn-primary"
            >
              Setzen
            </button>
          </div>
        </div>
      </div>

      {isReadOnly && (
        <p className="text-xs text-slate-400 mt-2">Read-only-Operator; Zuordnung kann nicht geändert werden.</p>
      )}
    </section>
  );
}
